import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Loader2, ArrowUpRight, Scissors } from "lucide-react";
import api from "@/lib/api";

const RANGES = [
  { label: "3 months+", days: 90 },
  { label: "6 months+", days: 180 },
  { label: "1 year+", days: 365 },
  { label: "2 years+", days: 730 },
];

const ago = (raw) => {
  const s = String(raw || "");
  const d = new Date(s.includes("T") ? s : s.replace(" UTC", "Z").replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "unknown";
  const months = Math.round((Date.now() - d.getTime()) / 2592000000);
  return months >= 12 ? `${Math.floor(months / 12)}y ${months % 12}mo ago` : `${months}mo ago`;
};

/** Iter 120 — KB branches nobody has touched, oldest first. */
export default function KbStaleReport() {
  const navigate = useNavigate();
  const [days, setDays] = usePersistedState("kb-stale.days", 365);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d) => {
    setLoading(true);
    api.get(`/kb/stale?days=${d}`)
      .then(({ data: res }) => setData(res))
      .catch(() => setData({ branches: [], count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  return (
    <section className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-stale-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Stale content</h2>
          {data && !loading && (
            <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300"
              data-testid="kb-stale-count">{data.count} branch{data.count === 1 ? "" : "es"}</span>
          )}
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r.days} type="button" data-testid={`kb-stale-range-${r.days}`}
              onClick={() => setDays(r.days)}
              className={`rounded-md border px-2 py-1 text-xs transition-colors
                ${days === r.days ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Categories whose newest article hasn&apos;t been updated in that window — review these first.
      </p>

      {loading && <Loader2 className="mt-4 h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
      {!loading && data?.branches?.length === 0 && (
        <p className="mt-4 text-sm text-slate-400 dark:text-slate-500" data-testid="kb-stale-empty">
          Nothing that old — the Knowledge Base is in good shape.
        </p>
      )}
      {!loading && data?.branches?.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 dark:divide-slate-800" data-testid="kb-stale-list">
          {data.branches.slice(0, 12).map((b) => (
            <li key={b.cat_id}>
              <button type="button" data-testid={`kb-stale-row-${b.slug}`}
                onClick={() => navigate(`/kb/c/${b.slug}`)}
                className="group flex w-full items-center gap-3 py-2 text-left">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-primary">
                    {b.name}
                  </span>
                  {b.path && <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">{b.path}</span>}
                </span>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{b.articles} article{b.articles === 1 ? "" : "s"}</span>
                <span className="w-24 shrink-0 text-right text-xs font-medium text-amber-600">{ago(b.last_updated)}</span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600 group-hover:text-primary" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && data?.count > 12 && (
        <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-stale-more">
          Showing the 12 oldest of {data.count}.
        </p>
      )}

      {/* Iter 126 — snippets sit in one flat category, so they never surface in
          the branch roll-up above. Listed on their own. */}
      {!loading && data?.snippet_total > 0 && (
        <div className="mt-5 border-t border-slate-100 dark:border-slate-800 pt-4" data-testid="kb-stale-snippets">
          <div className="flex items-center gap-2">
            <Scissors className="h-3.5 w-3.5 text-violet-500" />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Stale snippets</h3>
            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
              data-testid="kb-stale-snippet-count">
              {data.snippet_count} of {data.snippet_total}
            </span>
          </div>
          {data.snippet_count === 0 ? (
            <p className="mt-2 text-sm text-slate-400 dark:text-slate-500" data-testid="kb-stale-snippets-empty">
              Every Intercom snippet has been touched inside this window.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800" data-testid="kb-stale-snippet-list">
              {data.snippets.slice(0, 8).map((s) => (
                <li key={s.article_id}>
                  <button type="button" data-testid={`kb-stale-snippet-${s.slug}`}
                    onClick={() => navigate(`/kb/a/${s.slug}`)}
                    className="group flex w-full items-center gap-3 py-2 text-left">
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200 group-hover:text-primary">
                      {s.name}
                    </span>
                    <span className="w-24 shrink-0 text-right text-xs font-medium text-amber-600">{ago(s.last_updated)}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-300 dark:text-slate-600 group-hover:text-primary" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {data.snippet_count > 8 && (
            <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-stale-snippet-more">
              Showing the 8 oldest of {data.snippet_count} — edit snippets in Intercom; the nightly sync brings changes back.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
